/**
 * usageReport — data layer for the top-level Usage surface (management loop,
 * stage 4: "the report you forward").
 *
 * Reads the /api/yagni-code-usage endpoints (ADR-0028: sell-side
 * numbers only, admin-gated server-side). Everything here is a pure fold over
 * those responses so the narrative and the tables can be unit-tested without
 * mounting the surface.
 */
import { useEffect, useState } from "react";
import type {
  YagniCodeActivationMember,
  YagniCodeActivationResponse,
  YagniCodeAgentRunsResponse,
  YagniCodeAgentRunsSort,
  YagniCodeFrontierBasis,
  YagniCodePersonalTeamUsageResponse,
  YagniCodePersonalUsageResponse,
  YagniCodeTeamAgentUsageCell,
  YagniCodeTeamAgentUsageReport,
  YagniCodeTeamOwner,
  YagniCodeTeamUsageTotal,
  YagniCodeUsageDeveloperRow,
  YagniCodeUsageReport,
} from "@yagni/shared";

/** The server said no: the caller is not a workspace admin. */
export class UsageAccessError extends Error {
  constructor() {
    super("Workspace admin role is required to view usage.");
    this.name = "UsageAccessError";
  }
}

export interface UsageRange {
  /** Inclusive UTC day, YYYY-MM-DD. */
  from: string;
  /** Exclusive UTC day, YYYY-MM-DD. */
  to: string;
}

function utcDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Month-to-date in UTC: the 1st of the current month through tomorrow (exclusive). */
export function monthToDateRange(now: Date): UsageRange {
  const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
  return { from: utcDay(from), to: utcDay(to) };
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

export function monthLabel(now: Date): string {
  return MONTHS[now.getUTCMonth()] ?? "";
}

/** Millicents (1 = $0.00001) as customer-facing dollars and cents. */
export function formatUsd(millicents: number): string {
  return `$${(millicents / 100_000).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Millicents as whole customer-facing dollars: 128_400_000 → "$1,284". */
export function formatWholeUsd(millicents: number): string {
  const dollars = Math.round(millicents / 100_000);
  return `$${dollars.toLocaleString("en-US")}`;
}

/**
 * The Usage page's one dollar helper. Whole dollars by default ("$1,284"),
 * the headline's voice; `cents` keeps the ledger's two decimals where the
 * report always printed them ("$337.12").
 */
export function usd(millicents: number, options: { cents?: boolean } = {}): string {
  return options.cents ? formatUsd(millicents) : formatWholeUsd(millicents);
}

/** Whole dollars already in dollars (a plan price): 2500 → "$2,500". */
export function usdWhole(dollars: number): string {
  return `$${Math.round(dollars).toLocaleString("en-US")}`;
}

/** Savings share of the Claude list comparison (matched tier), in whole
 * percent, floored: when in doubt, understate. Null when the comparison is
 * zero or missing, and null when there are no savings to claim (zero or
 * negative): "saved -12%" is not a savings story, it is the no-savings state.
 * Never a guessed figure. */
export function savingsPercent(report: YagniCodeUsageReport): number | null {
  return shareOf(report.totalSavingsMillicents, report.totalCounterfactualMillicents);
}

function shareOf(savedMillicents: number, claudeMillicents: number): number | null {
  if (claudeMillicents <= 0) return null;
  if (savedMillicents <= 0) return null;
  return Math.floor((savedMillicents / claudeMillicents) * 100);
}

/** One basis's saved share of its Claude price, same rules as
 * {@link savingsPercent}. */
export function basisSavingsPercent(basis: YagniCodeFrontierBasis): number | null {
  return shareOf(basis.savingsMillicents, basis.totalMillicents);
}

/** Developers with a seat (excludes the unattributed bucket row). */
export function seatedDeveloperCount(report: YagniCodeUsageReport): number {
  return report.developers.filter((row) => row.userId !== null).length;
}

/**
 * The organization's headline: developers and Agents together, priced against
 * Claude at list on the matched tier (each dispatch at the Claude model mapped
 * to the tier it was sold as). Composed only from numbers the two reports
 * hold.
 */
export interface OrganizationHeadline {
  billedMillicents: number;
  developersMillicents: number;
  /** Null when the Agent report could not be loaded: the headline then covers
   * developers only, and says so. */
  agentsMillicents: number | null;
  claudeMillicents: number;
  savedMillicents: number;
  /** The Claude prompt-writing share inside `claudeMillicents`. */
  promptWritingMillicents: number;
  percent: number | null;
  /** Some spend had no Claude list equivalent, so the comparison covers part
   * of the bill rather than every dollar. */
  incomplete: boolean;
  dispatches: number;
}

/** One half of a combined Claude comparison: what it billed, its Claude
 * figure, its own floored savings, and whether every dollar was priced. */
export interface ClaudeSavingsPart {
  billedMillicents: number;
  claudeMillicents: number;
  savedMillicents: number;
  incomplete: boolean;
}

/**
 * Developers and Agents (or code and Agents on the admin panel) as one Claude
 * comparison. Savings floor once at the combined total when every dollar was
 * priced: summing each part's own floor would let one part's shortfall hide
 * inside the other's savings. When some spend had no Claude equivalent, the
 * billed total covers more than the priced part, so the parts' own priced
 * savings are the honest figure instead.
 */
export function combineClaudeSavings(parts: readonly ClaudeSavingsPart[]): {
  claudeMillicents: number;
  savedMillicents: number;
  percent: number | null;
  incomplete: boolean;
} {
  let billed = 0;
  let claude = 0;
  let partSaved = 0;
  let incomplete = false;
  for (const part of parts) {
    billed += part.billedMillicents;
    claude += part.claudeMillicents;
    partSaved += part.savedMillicents;
    incomplete ||= part.incomplete;
  }
  const savedMillicents = incomplete ? partSaved : Math.max(0, claude - billed);
  return {
    claudeMillicents: claude,
    savedMillicents,
    percent: shareOf(savedMillicents, claude),
    incomplete,
  };
}

export function organizationHeadline(
  developers: YagniCodeUsageReport,
  agents: Pick<YagniCodeTeamAgentUsageReport, "totalSellMillicents" | "totalDispatches" | "frontier"> | null,
): OrganizationHeadline {
  const agentBasis = agents?.frontier.matched ?? null;
  const agentsMillicents = agents ? agents.totalSellMillicents : null;
  const combined = combineClaudeSavings([
    {
      billedMillicents: developers.totalSellMillicents,
      claudeMillicents: developers.totalCounterfactualMillicents,
      savedMillicents: developers.totalSavingsMillicents,
      incomplete: developers.counterfactualIncomplete,
    },
    ...(agents && agentBasis
      ? [
          {
            billedMillicents: agents.totalSellMillicents,
            claudeMillicents: agentBasis.totalMillicents,
            savedMillicents: agentBasis.savingsMillicents,
            incomplete: agents.frontier.counterfactualIncomplete,
          },
        ]
      : []),
  ]);
  return {
    billedMillicents: developers.totalSellMillicents + (agentsMillicents ?? 0),
    developersMillicents: developers.totalSellMillicents,
    agentsMillicents,
    claudeMillicents: combined.claudeMillicents,
    savedMillicents: combined.savedMillicents,
    promptWritingMillicents:
      (developers.totalPromptWritingMillicents ?? 0) + (agentBasis?.promptWritingMillicents ?? 0),
    percent: combined.percent,
    incomplete: combined.incomplete,
    dispatches: developers.totalDispatches + (agents?.totalDispatches ?? 0),
  };
}

/**
 * The report's opening sentence, written to be forwarded as-is. Composed
 * only from numbers the report actually holds; when the Claude comparison is
 * absent the sentence says less rather than guessing.
 */
export function usageNarrative(
  report: YagniCodeUsageReport,
  month: string,
  headline: OrganizationHeadline = organizationHeadline(report, null),
): string {
  if (headline.dispatches === 0) {
    return `No usage recorded yet in ${month}.`;
  }
  const billed = formatWholeUsd(headline.billedMillicents);
  const developers = seatedDeveloperCount(report);
  // With no seated developers (all spend unattributed) the sentence drops the
  // clause: "across 0 developers" would read as a data bug, not a fact.
  const developerClause =
    developers > 0 ? `${developers} developer${developers === 1 ? "" : "s"}` : null;
  const hasAgents = headline.agentsMillicents !== null && headline.agentsMillicents > 0;
  let who: string | null;
  if (!hasAgents) {
    who = developerClause;
  } else if (developerClause) {
    who = `${developerClause} and your Agents`;
  } else {
    who = "your Agents";
  }
  if (headline.percent === null) {
    return who
      ? `YAGNI billed ${billed} across ${who} in ${month} so far.`
      : `YAGNI billed ${billed} in ${month} so far.`;
  }
  const claude = formatWholeUsd(headline.claudeMillicents);
  const saved = formatWholeUsd(headline.savedMillicents);
  const closing = who
    ? `${saved} saved (${headline.percent}%) across ${who}.`
    : `${saved} saved (${headline.percent}%).`;
  return (
    `YAGNI billed ${billed} in ${month} so far, against ${claude} the same work ` +
    `would have cost at Claude list prices. ${closing}`
  );
}

export interface ActivationSplit {
  /** Authenticated API use within the last 7 days. */
  activeThisWeek: YagniCodeActivationMember[];
  /** Has a device token, but no use in the last 7 days (or never used it). */
  quiet: YagniCodeActivationMember[];
  /** Never minted a token — has not started. */
  notStarted: YagniCodeActivationMember[];
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

export function splitActivation(
  members: YagniCodeActivationMember[],
  nowMs: number,
): ActivationSplit {
  const split: ActivationSplit = { activeThisWeek: [], quiet: [], notStarted: [] };
  for (const member of members) {
    if (!member.hasToken) {
      split.notStarted.push(member);
      continue;
    }
    const lastUsed = member.lastUsedAt ? Date.parse(member.lastUsedAt) : Number.NaN;
    if (Number.isFinite(lastUsed) && nowMs - lastUsed <= WEEK_MS) {
      split.activeThisWeek.push(member);
    } else {
      split.quiet.push(member);
    }
  }
  return split;
}

export function memberDisplayName(member: {
  displayName: string | null;
  email: string | null;
}): string {
  return member.displayName ?? member.email ?? "Former member";
}

async function fetchUsageReport(range: UsageRange): Promise<YagniCodeUsageReport> {
  const res = await fetch(
    `/api/yagni-code-usage?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`,
    { credentials: "include" },
  );
  if (res.status === 403) throw new UsageAccessError();
  if (!res.ok) throw new Error(`Failed to load usage (${res.status})`);
  const data = (await res.json()) as { usage: YagniCodeUsageReport };
  return data.usage;
}

async function fetchActivation(): Promise<YagniCodeActivationMember[]> {
  const res = await fetch("/api/yagni-code-usage/activation", { credentials: "include" });
  if (res.status === 403) throw new UsageAccessError();
  if (!res.ok) throw new Error(`Failed to load activation (${res.status})`);
  const data = (await res.json()) as YagniCodeActivationResponse;
  return data.members;
}

async function fetchPersonalUsage(range: UsageRange): Promise<YagniCodePersonalUsageResponse> {
  const res = await fetch(
    `/api/yagni-code-usage/me?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`,
    { credentials: "include" },
  );
  if (!res.ok) throw new Error(`Failed to load usage (${res.status})`);
  return (await res.json()) as YagniCodePersonalUsageResponse;
}

/**
 * The numbers the sidebar meter needs, normalized across the two scopes:
 * the workspace report (admins and owners) and the caller's own line (/me,
 * everyone else). "Whose money" differs; the shape does not.
 */
export interface UsageSummary {
  scope: "workspace" | "me";
  billedMillicents: number;
  /** The same work at Claude list prices, matched tier. */
  claudeMillicents: number;
  savedMillicents: number;
  /** The Claude prompt-writing share inside `claudeMillicents`. */
  promptWritingMillicents: number;
  dispatches: number;
}

export function summarizeReport(report: YagniCodeUsageReport): UsageSummary {
  return {
    scope: "workspace",
    billedMillicents: report.totalSellMillicents,
    claudeMillicents: report.totalCounterfactualMillicents,
    savedMillicents: report.totalSavingsMillicents,
    promptWritingMillicents: report.totalPromptWritingMillicents ?? 0,
    dispatches: report.totalDispatches,
  };
}

export function summarizeRow(row: YagniCodeUsageDeveloperRow): UsageSummary {
  return {
    scope: "me",
    billedMillicents: row.totalSellMillicents,
    claudeMillicents: row.totalCounterfactualMillicents,
    savedMillicents: row.totalSavingsMillicents,
    promptWritingMillicents: row.totalPromptWritingMillicents ?? 0,
    dispatches: row.totalDispatches,
  };
}

/**
 * Month-to-date summary for the sidebar meter. Scope decides the read:
 * workspace (admin rollup) or the caller's own line. Any failure resolves to
 * null — a quiet rail beats an error card in the chrome.
 */
export function useUsageSummary(scope: "workspace" | "me"): UsageSummary | null {
  const [summary, setSummary] = useState<UsageSummary | null>(null);
  useEffect(() => {
    let cancelled = false;
    setSummary(null);
    void (async () => {
      try {
        const range = monthToDateRange(new Date());
        if (scope === "workspace") {
          const report = await fetchUsageReport(range);
          if (!cancelled) setSummary(summarizeReport(report));
        } else {
          const personal = await fetchPersonalUsage(range);
          if (!cancelled) setSummary(personal.row ? summarizeRow(personal.row) : null);
        }
      } catch {
        // Quiet: the meter and the Usage rail row simply stay hidden.
        if (!cancelled) setSummary(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scope]);
  return summary;
}

/** Savings share of the Claude list comparison for a summary. Same rules as
 * {@link savingsPercent}. */
export function summarySavingsPercent(summary: UsageSummary): number | null {
  return shareOf(summary.savedMillicents, summary.claudeMillicents);
}

export interface PersonalUsageState {
  row: YagniCodeUsageDeveloperRow | null;
  error: string | null;
  loading: boolean;
}

/** Month-to-date personal line for the member view of /usage. */
export function usePersonalUsage(): PersonalUsageState {
  const [state, setState] = useState<PersonalUsageState>({
    row: null,
    error: null,
    loading: true,
  });
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const personal = await fetchPersonalUsage(monthToDateRange(new Date()));
        if (!cancelled) setState({ row: personal.row, error: null, loading: false });
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : "Failed to load usage";
        setState({ row: null, error: message, loading: false });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return state;
}

export interface UsageReportState {
  report: YagniCodeUsageReport | null;
  /** The Agents' month-to-date spend, Team x Agent. Null when it could not be
   * loaded: the headline then covers developers only. */
  agents: YagniCodeTeamAgentUsageReport | null;
  activation: YagniCodeActivationMember[] | null;
  /** The server refused: not a workspace admin. */
  forbidden: boolean;
  error: string | null;
  loading: boolean;
}

/**
 * Month-to-date report + activation. `withActivation: false` is the sidebar
 * meter's cheap mode — one request, and any failure resolves to a hidden
 * meter rather than an error state.
 */
export function useUsageReport(options: { withActivation?: boolean } = {}): UsageReportState {
  const withActivation = options.withActivation ?? true;
  const [state, setState] = useState<UsageReportState>({
    report: null,
    agents: null,
    activation: null,
    forbidden: false,
    error: null,
    loading: true,
  });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const range = monthToDateRange(new Date());
        const [report, agents, activation] = await Promise.all([
          fetchUsageReport(range),
          // A failed Agent read costs the Agents' share of the headline,
          // never the developer figures.
          fetchTeamAgentUsage(range).catch(() => null),
          withActivation ? fetchActivation() : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setState({ report, agents, activation, forbidden: false, error: null, loading: false });
      } catch (err) {
        if (cancelled) return;
        const empty = { report: null, agents: null, activation: null, loading: false };
        if (err instanceof UsageAccessError) {
          setState({ ...empty, forbidden: true, error: null });
        } else {
          const message = err instanceof Error ? err.message : "Failed to load usage";
          setState({ ...empty, forbidden: false, error: message });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [withActivation]);

  return state;
}

/* ─── Teams and Agents: the pivot and the drills ─────────────── */

function rangeQuery(range: UsageRange): string {
  return `from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`;
}

export async function fetchTeamAgentUsage(
  range: UsageRange,
): Promise<YagniCodeTeamAgentUsageReport> {
  const res = await fetch(`/api/yagni-code-usage/teams?${rangeQuery(range)}`, {
    credentials: "include",
  });
  if (res.status === 403) throw new UsageAccessError();
  if (!res.ok) throw new Error(`Failed to load Team usage (${res.status})`);
  return (await res.json()) as YagniCodeTeamAgentUsageReport;
}

/** The Agents' spend on the caller's own personal Teams (any member). */
export async function fetchPersonalTeamUsage(
  range: UsageRange,
): Promise<YagniCodePersonalTeamUsageResponse> {
  const res = await fetch(`/api/yagni-code-usage/me/teams?${rangeQuery(range)}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to load your personal Team usage (${res.status})`);
  return (await res.json()) as YagniCodePersonalTeamUsageResponse;
}

export interface PersonalTeamUsageState {
  report: YagniCodePersonalTeamUsageResponse | null;
  error: string | null;
  loading: boolean;
}

/** Month-to-date personal Team spend for the member view of /usage. Loads
 * on its own, so a failure here costs only this block, never the line. */
export function usePersonalTeamUsage(): PersonalTeamUsageState {
  const [state, setState] = useState<PersonalTeamUsageState>({
    report: null,
    error: null,
    loading: true,
  });
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const report = await fetchPersonalTeamUsage(monthToDateRange(new Date()));
        if (!cancelled) setState({ report, error: null, loading: false });
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : "Failed to load your personal Team usage";
        // The section renders nothing on a failure, so say it once here. The
        // message is the request's status line, never the member's data.
        console.warn("[usage] personal_team_usage.read_failed", message);
        setState({ report: null, error: message, loading: false });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return state;
}

/** One of the caller's personal Teams, as the member's Usage page lists it. */
export interface PersonalTeamBlock {
  deskId: string;
  deskName: string | null;
  archived: boolean;
  /** The Team's total across its Agents; null when it had no Agent spend. */
  total: YagniCodeTeamUsageTotal | null;
  /** One per Agent that worked on it, by spend. */
  cells: YagniCodeTeamAgentUsageCell[];
}

/** The caller's personal Teams: those with spend first (by spend), then the
 * quiet ones (by name). Empty when the caller has no personal Team. */
export function personalTeamBlocks(report: YagniCodePersonalTeamUsageResponse): PersonalTeamBlock[] {
  const withSpend: PersonalTeamBlock[] = report.teams.flatMap((team) =>
    // Defense in depth: the personal read never returns the No Team bucket.
    team.deskId === null
      ? []
      : [
          {
            deskId: team.deskId,
            deskName: team.deskName,
            archived: team.archived === true,
            total: team,
            cells: report.cells.filter((cell) => cell.deskId === team.deskId),
          },
        ],
  );
  const quiet: PersonalTeamBlock[] = (report.idleTeams ?? []).map((team) => ({
    deskId: team.deskId,
    deskName: team.deskName,
    archived: false,
    total: null,
    cells: [],
  }));
  return [...withSpend, ...quiet];
}

export interface AgentRunsPage {
  sort: YagniCodeAgentRunsSort;
  limit: number;
  offset: number;
}

/** Thrown for an Agent the server does not know (404): a stale or typed URL. */
export class UnknownAgentError extends Error {
  constructor() {
    super("No such Agent.");
    this.name = "UnknownAgentError";
  }
}

export async function fetchAgentRuns(
  agentId: string,
  range: UsageRange,
  page: AgentRunsPage,
): Promise<YagniCodeAgentRunsResponse> {
  const res = await fetch(
    `/api/yagni-code-usage/agents/${encodeURIComponent(agentId)}/runs?${rangeQuery(range)}` +
      `&sort=${page.sort}&limit=${page.limit}&offset=${page.offset}`,
    { credentials: "include" },
  );
  if (res.status === 403) throw new UsageAccessError();
  if (res.status === 404) throw new UnknownAgentError();
  if (!res.ok) throw new Error(`Failed to load Agent runs (${res.status})`);
  return (await res.json()) as YagniCodeAgentRunsResponse;
}

/** Every run of the Agent in the range, as a spreadsheet. */
export function agentRunsCsvHref(agentId: string, range: UsageRange): string {
  return `/api/yagni-code-usage/agents/${encodeURIComponent(agentId)}/runs?${rangeQuery(range)}&format=csv`;
}

/** The "No Team" bucket's path segment on the Team drill. */
export const NO_TEAM_SEGMENT = "none";

/** The drill links carry the report's range, so a drill reads the same
 * window as the row it came from. */
export function agentDrillPath(agentId: string, range: UsageRange): string {
  return `/usage/agents/${encodeURIComponent(agentId)}?${rangeQuery(range)}`;
}

export function teamDrillPath(deskId: string | null, range: UsageRange): string {
  return `/usage/teams/${encodeURIComponent(deskId ?? NO_TEAM_SEGMENT)}?${rangeQuery(range)}`;
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** A drill's range from its query string, else month-to-date. Both ends
 * must be well-formed days in order, or the pair falls back: half a range
 * is not a range. */
export function rangeFromSearch(search: URLSearchParams, now: Date): UsageRange {
  const from = search.get("from");
  const to = search.get("to");
  if (from && to && ISO_DAY.test(from) && ISO_DAY.test(to) && from < to) {
    return { from, to };
  }
  return monthToDateRange(now);
}

/** "2026-10-01 through 2026-10-06": the exclusive `to` shown as the last
 * day it includes. */
export function rangeLabel(range: UsageRange): string {
  const last = new Date(Date.parse(`${range.to}T00:00:00.000Z`) - 24 * 60 * 60 * 1000);
  return `${range.from} through ${utcDay(last)}`;
}

export function teamLabel(team: { deskId: string | null; deskName: string | null }): string {
  if (team.deskId === null) return "No Team";
  return team.deskName ?? "Deleted Team";
}

/** A personal Team's owner as the report names them: display name, else
 * email. Null for a shared Team, or an owner with neither. */
export function teamOwnerName(team: { personalOwner?: YagniCodeTeamOwner | null }): string | null {
  const owner = team.personalOwner;
  if (!owner) return null;
  return owner.displayName ?? owner.email ?? null;
}

/** The quiet line under a Team's name: "Personal · Jack Collins",
 * "Archived", or both. Null when there is nothing to add. */
export function teamSublabel(team: {
  personalOwner?: YagniCodeTeamOwner | null;
  archived?: boolean;
}): string | null {
  const parts: string[] = [];
  if (team.personalOwner) {
    const owner = teamOwnerName(team);
    parts.push(owner ? `Personal · ${owner}` : "Personal");
  }
  if (team.archived) parts.push("Archived");
  return parts.length > 0 ? parts.join(" · ") : null;
}

/** The lede under a Team drill's title: No Team, a personal Team (owner
 * named when known), or a shared Team, each noting when it is archived. */
export function teamLede(
  rangeText: string,
  deskId: string | null,
  team: { personalOwner?: YagniCodeTeamOwner | null; archived?: boolean } | null,
): string {
  if (deskId === null) {
    return `${rangeText}. Agent work with no Team behind it, such as checking a repository's environment, kept here so the report adds up.`;
  }
  const archivedNote = team?.archived === true ? " Archived, its spend still counts." : "";
  if (team?.personalOwner) {
    const owner = teamOwnerName(team);
    const whose = owner ? `Personal Team of ${owner}.` : "Personal Team.";
    return `${rangeText}. ${whose}${archivedNote} What its Agents were charged, beside the same tokens at Claude list.`;
  }
  return `${rangeText}.${archivedNote} What this Team's Agents were charged, beside the same tokens at Claude list.`;
}

/** A Team's name for a screen reader, owner included, so two personal
 * Teams both called "Jack's Team" are told apart. */
export function teamAccessibleLabel(team: {
  deskId: string | null;
  deskName: string | null;
  personalOwner?: YagniCodeTeamOwner | null;
}): string {
  const label = teamLabel(team);
  if (!team.personalOwner) return label;
  const owner = teamOwnerName(team);
  return owner ? `${label} (personal, ${owner})` : `${label} (personal)`;
}

export function agentLabel(agent: {
  agentId: string;
  displayName: string | null;
  workerSlug: string | null;
}): string {
  if (agent.displayName) return agent.displayName;
  const slug = agent.workerSlug ?? agent.agentId;
  return slug.charAt(0).toUpperCase() + slug.slice(1);
}

/** The order a ticket meets the Agents: Bailey triages, Reeve plans, Wright
 * builds, Proctor reviews, Fletcher tests, Harper reports. */
const AGENT_PIPELINE_ORDER = ["bailey", "reeve", "wright", "proctor", "fletcher", "harper"];

/** Agents in pipeline order. An Agent outside the pipeline follows in the
 * order it came (by spend), and Team runs, which have no Agent, come last. */
export function orderAgents<T extends { agentId: string; workerSlug: string | null }>(
  agents: readonly T[],
): T[] {
  const rank = (agent: T) => {
    if (agent.workerSlug === null) return AGENT_PIPELINE_ORDER.length + 1;
    const at = AGENT_PIPELINE_ORDER.indexOf(agent.workerSlug);
    return at === -1 ? AGENT_PIPELINE_ORDER.length : at;
  };
  return agents
    .map((agent, index) => ({ agent, index }))
    .sort((a, b) => rank(a.agent) - rank(b.agent) || a.index - b.index)
    .map(({ agent }) => agent);
}

/** What one run of each Agent is called on its drill: "Largest reviews". */
const AGENT_RUN_NOUNS: Record<string, { one: string; many: string }> = {
  wright: { one: "build", many: "builds" },
  code_review: { one: "review", many: "reviews" },
  reeve: { one: "plan", many: "plans" },
  fletcher: { one: "QA run", many: "QA runs" },
  desk_brief: { one: "report", many: "reports" },
  proposal_lane: { one: "proposal", many: "proposals" },
};

export function agentRunNoun(agentId: string): { one: string; many: string } {
  return AGENT_RUN_NOUNS[agentId] ?? { one: "run", many: "runs" };
}

export type FrontierBasisKey = "standard" | "matched" | "advanced";

/** The drill's basis toggle, in the order it shows them. */
export const FRONTIER_BASES: ReadonlyArray<{ key: FrontierBasisKey; label: string }> = [
  { key: "standard", label: "All Claude Sonnet" },
  { key: "matched", label: "Matched Claude tier" },
  { key: "advanced", label: "All Claude Opus" },
];

/** A team would otherwise run its PR reviews on Claude Sonnet, so Proctor's
 * drill opens there; every other Agent opens on the matched tier, the
 * report's own basis. */
export function defaultBasisFor(agentId: string): FrontierBasisKey {
  return agentId === "code_review" ? "standard" : "matched";
}
